// The project on a mind map (design-docs/Maps — design.md ▸ Bringing the project in, Turn Into Scenes…), worked out
// apart from React so it's tested in Node: what a linked card shows (its title, the POV's colour, "Ch. 1 · Enfield ·
// Revised"), the titles a map's linked notes should hold, where a note added from the project goes, what Add to Map…
// offers, where Turn Into Scenes… can put its documents, and the places stacks give their notes.
import {
  MapArrange, MapCast, MapLayout, type EntityKind, type MapContent, type MapFrame, type MapLink, type MapNote, type MapPoint, type OpenedProject, type Project,
  type TreeNode,
} from '@writing-suite/core';
import { cssColor } from '../helpers.ts';

type StoryEntity = OpenedProject['record']['entities'][number];

/** The project's characters, places, objects and motifs, with what the canvas needs of them. */
export function castOf(project: Project): StoryEntity[] {
  const record = (project as Partial<OpenedProject>).record;
  return record ? record.entities : project.entities().map((e) => ({ ...e, notes: '' }));
}

/** Whether what a note links to is still there: a document the project knows (in the Trash or not), or someone in the
 *  cast. A link to something gone is drawn as a plain note with its words. */
export function linkLives(project: Pick<Project, 'fragment' | 'entities'>, link: MapLink | null): boolean {
  if (!link) return false;
  return link.kind === 'fragment' ? project.fragment(link.id) !== null : project.entities().some((e) => e.id === link.id);
}

/** The title a linked note shows and keeps as its words (`MindMapController.shownTitle`): a document's title; for someone
 *  in the cast, the note's own words (trimmed) when they already name them — their name or an alias, as Link Notes to
 *  Cast compares names — else their name. Null when what it links to is gone. */
export function linkTitle(project: Pick<Project, 'fragment' | 'entities'>, link: MapLink, words = ''): string | null {
  if (link.kind === 'fragment') {
    const f = project.fragment(link.id);
    return f ? f.title.trim() || 'Untitled' : null;
  }
  const e = project.entities().find((x) => x.id === link.id);
  if (!e) return null;
  const own = MapCast.key(words);
  return own !== '' && [e.name, ...e.aliases].some((name) => MapCast.key(name) === own) ? words.trim() : e.name;
}

/** A note's title as shown: its link's (`linkTitle`), else its own words. */
export const shownTitle = (project: Pick<Project, 'fragment' | 'entities'>, note: Pick<MapNote, 'link' | 'text'>): string =>
  (note.link ? linkTitle(project, note.link, note.text) : null) ?? note.text;

/** The linked notes whose words are no longer their document's title (or the name they show): note → the title now. */
export function staleTitles(project: Pick<Project, 'fragment' | 'entities'>, content: MapContent): Map<string, string> {
  const out = new Map<string, string>();
  for (const n of content.notes) {
    if (!n.link) continue;
    const title = linkTitle(project, n.link, n.text);
    if (title !== null && title !== n.text) out.set(n.id, title);
  }
  return out;
}

/** "Ch. 14" for each document in a numbered chapter (itself or the nearest folder above it the binder numbers as a
 *  chapter), by fragment id. */
export function chapterLabels(project: Project): Map<string, string> {
  const out = new Map<string, string>();
  const manuscript = project.manuscriptId();
  if (!manuscript) return out;
  const openers = project.openers();
  const walk = (nodes: TreeNode[], chapter: string | null) => {
    for (const n of nodes) {
      const o = n.fragmentId ? openers.get(n.fragmentId) : undefined;
      const here = o && o.kind === 'chapter' && o.number !== null ? `Ch. ${o.number}` : chapter;
      if (n.fragmentId && here && !out.has(n.fragmentId)) out.set(n.fragmentId, here);
      walk(n.children, here);
    }
  };
  walk(project.tree(manuscript), null);
  return out;
}

/** What a document's card shows. */
export interface CardInfo {
  title: string;
  synopsis: string;
  trashed: boolean;
  /** The strip along the top: the POV's colour, else the label's (a CSS colour), or none. */
  edge: string | null;
  /** "Ch. 1 · Enfield · Revised": the chapter, the setting and the status that it has. */
  detail: string;
}

export function cardInfo(project: Project, fragmentId: string, chapters: ReadonlyMap<string, string>): CardInfo | null {
  const f = project.fragment(fragmentId);
  if (!f) return null;
  const meta = f.metadata ?? {};
  const text = (key: string) => (meta[key]?.type === 'text' ? (meta[key] as { value: string }).value : null);
  const entities = project.entities();
  const pov = entities.find((e) => e.id === text('pov'));
  const setting = entities.find((e) => e.id === text('setting'));
  const labelValue = text('label');
  const label = labelValue ? project.fields().find((x) => x.id === 'label')?.options.find((o) => o.id === labelValue || o.name === labelValue) : undefined;
  const status = meta.status ? project.displayValue('status', meta.status) : '';
  return {
    title: f.title.trim() || 'Untitled',
    synopsis: f.synopsis.trim(),
    trashed: f.trashed,
    edge: cssColor(pov?.color) ?? cssColor(label?.color) ?? null,
    detail: [chapters.get(fragmentId) ?? '', setting?.name ?? '', status].filter(Boolean).join(' · '),
  };
}

// MARK: Adding to the map

/** Where something added from the project goes: to the right of everything on the map, level with its top, nudged clear
 *  (the middle of the paper on an empty map). */
export function placeToRight(content: MapContent, frames: ReadonlyMap<string, MapFrame>, size: { width: number; height: number }): MapPoint {
  const bounds = MapLayout.bounds(content, frames);
  const start = bounds ? { x: bounds.x + bounds.width + MapLayout.childGap, y: bounds.y } : { x: 0, y: 0 };
  const placed = MapLayout.nudged({ ...start, width: size.width, height: size.height }, MapLayout.rooms(content, frames));
  return { x: placed.x, y: placed.y };
}

/** Where dropped documents go: the first at the drop, the rest 30 points apart down. */
export const dropPlaces = (at: MapPoint, count: number): MapPoint[] => Array.from({ length: count }, (_, i) => ({ x: at.x, y: at.y + i * 30 }));

/** The kinds Add to Map… offers from the cast, in this order. */
export const CAST_KINDS: readonly EntityKind[] = ['character', 'place', 'object', 'motif'];
export const CAST_KIND_NAMES: Record<string, string> = { character: 'Characters', place: 'Places', object: 'Objects', motif: 'Motifs' };

/** A choice in Add to Map… and Link…. */
export type MapChoice = { kind: 'fragment'; id: string; title: string; place: string } | { kind: 'entity'; id: string; title: string; place: string; color: string | null };

/** The cast for Add to Map…: characters, places, objects and motifs, each kind in name order. */
export function castChoices(project: Project): MapChoice[] {
  const entities = project.entities();
  return CAST_KINDS.flatMap((kind) =>
    entities.filter((e) => e.kind === kind).sort((a, b) => a.name.localeCompare(b.name)).map((e): MapChoice => ({
      kind: 'entity', id: e.id, title: e.name || 'Unnamed', place: CAST_KIND_NAMES[kind], color: e.color ?? null,
    })),
  );
}

const COMBINING = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

/** A choice's words matched by a find field: case and accents aside. */
export function matchesFind(choice: Pick<MapChoice, 'title'>, find: string): boolean {
  const fold = (s: string) => s.normalize('NFD').replace(COMBINING, '').toLocaleLowerCase();
  const query = fold(find.trim());
  return query === '' || fold(choice.title).includes(query);
}

// MARK: Turn Into Scenes…

/** A folder of the manuscript Turn Into Scenes… can fill, with how deep it is. */
export interface SceneFolder { nodeId: string; title: string; depth: number }

/** The manuscript's folders, in reading order. */
export function manuscriptFolders(project: Project): SceneFolder[] {
  const manuscript = project.manuscriptId();
  if (!manuscript) return [];
  const out: SceneFolder[] = [];
  const walk = (nodes: TreeNode[], depth: number) => {
    for (const n of nodes) {
      if (n.isFolder || n.children.length) out.push({ nodeId: n.id, title: n.title || 'Untitled', depth });
      walk(n.children, depth + 1);
    }
  };
  walk(project.tree(manuscript), 0);
  return out;
}

// MARK: Stacks as drawn

/** Each note's place as drawn: its own, or under its stack's head. A note dragged on its own out of its stack (`loose`)
 *  is drawn where it's dragged; a dragged head carries its stack. `offset` moves the dragged notes. */
export function shownPlaces(content: MapContent, sizes: ReadonlyMap<string, { width: number; height: number }>, moving?: { ids: ReadonlySet<string>; dx: number; dy: number }): {
  notes: MapNote[];
  frames: Map<string, MapFrame>;
} {
  const size = (id: string) => sizes.get(id) ?? { width: 64, height: 24 };
  const frameOf = (n: MapNote): MapFrame => ({ x: n.at.x, y: n.at.y, ...size(n.id) });
  const base = new Map(content.notes.map((n) => [n.id, frameOf(n)]));
  const stacked = MapArrange.stackPlaces(content, base);
  let notes = content.notes.map((n) => (stacked.has(n.id) ? { ...n, at: stacked.get(n.id)! } : n));
  if (moving && (moving.dx !== 0 || moving.dy !== 0 || moving.ids.size)) {
    const headOf = new Map<string, string>();
    for (const [head, members] of MapArrange.stacks(content)) for (const m of members) headOf.set(m, head);
    const loose = new Set([...moving.ids].filter((id) => headOf.has(id) && !moving.ids.has(headOf.get(id)!)));
    notes = notes.map((n) => {
      if (!moving.ids.has(n.id)) return n;
      const at = { x: n.at.x + moving.dx, y: n.at.y + moving.dy };
      return loose.has(n.id) ? { ...n, at, stack: null, stackOrder: null } : { ...n, at };
    });
    const again = { ...content, notes };
    const places = MapArrange.stackPlaces(again, new Map(notes.map((n) => [n.id, frameOf(n)])));
    notes = notes.map((n) => (places.has(n.id) ? { ...n, at: places.get(n.id)! } : n));
  }
  return { notes, frames: new Map(notes.map((n) => [n.id, frameOf(n)])) };
}

/** The ids of the notes in stacks: a member → its head. */
export function stackHeads(content: MapContent): Map<string, string> {
  const out = new Map<string, string>();
  for (const [head, members] of MapArrange.stacks(content)) for (const m of members) out.set(m, head);
  return out;
}

/** The pencil line beside a stack: 10 points left of the column, from the head's top to the last note's bottom. */
export function stackLine(head: MapFrame, last: MapFrame): [MapPoint, MapPoint] {
  return [{ x: head.x - 10, y: head.y }, { x: head.x - 10, y: last.y + last.height }];
}

/** The smallest a region is resized to. */
export const REGION_MIN = { width: 60, height: 40 };

/** Where an animation between two sets of places is at `t` (0–1, eased). */
export function between(from: ReadonlyMap<string, MapPoint>, to: ReadonlyMap<string, MapPoint>, t: number): Map<string, MapPoint> {
  const e = t >= 1 ? 1 : 1 - Math.pow(1 - Math.max(0, t), 3);
  const out = new Map<string, MapPoint>();
  for (const [id, b] of to) {
    const a = from.get(id) ?? b;
    out.set(id, { x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e });
  }
  return out;
}
